import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";

/**
 * Interactive earnings calculator for the /advertise page.
 *
 * Advertiser convertes on tangible ROI. We show them: given typical rate + booking
 * frequency, how much they'd earn per month on Vixera (net, after our optional
 * featured boost cost).
 */
const PRESET_RATES = [1500, 2500, 3500, 5000, 7500];
const BOOKINGS_MIN = 1;
const BOOKINGS_MAX = 20;

export default function EarningsCalculator() {
  const [rate, setRate] = useState(2500);
  const [bookings, setBookings] = useState(6);
  const [featured, setFeatured] = useState(true);

  const monthly = useMemo(() => {
    const gross = rate * bookings * 4.33;  // weeks per month
    const boostCost = featured ? 899 : 0;  // 1-month featured boost
    return { gross, net: gross - boostCost, boostCost };
  }, [rate, bookings, featured]);

  return (
    <div
      data-testid="earnings-calculator"
      className="relative overflow-hidden border border-velvet-gold/30 bg-gradient-to-br from-velvet-coal via-velvet-ink to-black p-8 md:p-12"
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2 }}
        className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-velvet-gold/20 blur-3xl"
      />
      <div className="relative">
        <div className="mb-8 flex items-center gap-3">
          <TrendingUp className="text-velvet-gold" size={18} />
          <p className="text-xs uppercase tracking-luxe text-velvet-gold">Your potential income</p>
        </div>

        <h3 className="mb-8 font-heading text-3xl font-light text-white sm:text-4xl md:text-5xl">
          What you could <span className="italic text-velvet-rose">earn</span> per month
        </h3>

        {/* Rate presets */}
        <div className="mb-8">
          <label className="mb-4 block text-[10px] uppercase tracking-luxe text-white/50">
            Your typical rate per booking
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESET_RATES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRate(r)}
                data-testid={`calc-rate-${r}`}
                className={`rounded-full border px-5 py-2 text-xs uppercase tracking-luxe transition-colors ${
                  rate === r
                    ? "border-velvet-gold bg-velvet-gold text-black"
                    : "border-white/15 text-white/70 hover:border-white/40"
                }`}
              >
                R{r.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Bookings slider */}
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <label className="text-[10px] uppercase tracking-luxe text-white/50">
              Bookings per week
            </label>
            <span className="text-lg tabular-nums text-velvet-gold">{bookings}</span>
          </div>
          <input
            type="range"
            min={BOOKINGS_MIN}
            max={BOOKINGS_MAX}
            value={bookings}
            onChange={(e) => setBookings(Number(e.target.value))}
            data-testid="calc-bookings"
            className="w-full accent-velvet-rose"
          />
          <div className="mt-1 flex justify-between text-[10px] uppercase tracking-luxe text-white/30">
            <span>{BOOKINGS_MIN}</span>
            <span>{BOOKINGS_MAX}+</span>
          </div>
        </div>

        {/* Featured toggle */}
        <label className="mb-10 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={featured}
            onChange={(e) => setFeatured(e.target.checked)}
            data-testid="calc-featured"
            className="mt-1 accent-velvet-gold"
          />
          <div>
            <p className="text-sm text-white">Featured listing boost (recommended)</p>
            <p className="text-xs font-light text-white/50">
              R899 / month · appears at the top of search + city landing pages
            </p>
          </div>
        </label>

        {/* Result */}
        <div className="border-t border-white/10 pt-8">
          <p className="text-[10px] uppercase tracking-luxe text-white/40">Projected monthly income</p>
          <motion.p
            key={monthly.net}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-2 font-heading text-5xl font-light text-velvet-gold sm:text-6xl md:text-7xl"
            data-testid="calc-result-net"
          >
            R{Math.round(monthly.net).toLocaleString()}
          </motion.p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <span className="text-white/50">
              Gross: <span className="tabular-nums text-white">R{Math.round(monthly.gross).toLocaleString()}</span>
            </span>
            {featured && (
              <span className="text-white/50">
                Featured boost: <span className="tabular-nums text-velvet-rose">−R{monthly.boostCost}</span>
              </span>
            )}
            <span className="text-white/50">
              Vixera keeps: <span className="tabular-nums text-velvet-gold">R0</span> from your bookings
            </span>
          </div>
          <p className="mt-6 text-[10px] uppercase tracking-luxe text-white/40">
            You keep 100% of every booking. Vixera charges only for optional featured slots.
          </p>
        </div>
      </div>
    </div>
  );
}
